'use client';

// import { useTranslations } from "next-intl";
import type { MenuItem } from '@workspace/ui/types';
import { ChevronDown, Menu, X } from 'lucide-react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';
import { cn, isSubmenuItemActive, pathnameMatchesMenuUrl, submenuMatchesPathname } from '../../lib';
import {
  Button,
  Logo,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  TruncateWithTooltip,
} from '../index';
import { SidebarOverlay } from './sidebar-overlay';

interface AppNavbarProps {
  menuItems: MenuItem[];
  isNotificationsOpen: boolean;
  setIsNotificationsOpen: (isOpen: boolean) => void;
  isOrganisationSwitcherOpen?: boolean;
  setIsOrganisationSwitcherOpen?: (isOpen: boolean) => void;
  profileInitials?: string;
  profileItems?: MenuItem[];
}

export function AppNavbar({
  menuItems,
  isNotificationsOpen,
  setIsNotificationsOpen,
  isOrganisationSwitcherOpen = false,
  setIsOrganisationSwitcherOpen,
  profileInitials,
  profileItems = [],
}: AppNavbarProps) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const [expandedSubMenus, setExpandedSubMenus] = useState<Set<string>>(new Set());
  const pathname = usePathname();
  const prevParentActiveByTitleRef = useRef<Map<string, boolean> | null>(null);
  const activeSubviewKey = isOrganisationSwitcherOpen
    ? 'organisationSwitcher'
    : isNotificationsOpen
      ? 'notifications'
      : null;

  useEffect(() => {
    const prevMap = prevParentActiveByTitleRef.current;
    const isFirstRun = prevMap === null;

    setExpandedSubMenus((current) => {
      const next = new Set(current);

      menuItems.forEach((menuItem) => {
        if (!menuItem.items?.length) return;
        const isParentActive = menuItem.items.some((sub) => submenuMatchesPathname(pathname, sub));

        if (isFirstRun) {
          if (isParentActive) next.add(menuItem.title);
        } else {
          const wasActive = prevMap.get(menuItem.title) ?? false;
          if (isParentActive && !wasActive) next.add(menuItem.title);
          if (!isParentActive && wasActive) next.delete(menuItem.title);
        }
      });

      return next;
    });

    const newMap = new Map<string, boolean>();
    menuItems.forEach((menuItem) => {
      if (!menuItem.items?.length) return;
      newMap.set(
        menuItem.title,
        menuItem.items.some((sub) => submenuMatchesPathname(pathname, sub))
      );
    });
    prevParentActiveByTitleRef.current = newMap;
  }, [pathname, menuItems]);

  const toggleSubMenu = (title: string) => {
    setExpandedSubMenus((prev) => {
      const next = new Set(prev);
      if (next.has(title)) next.delete(title);
      else next.add(title);
      return next;
    });
  };

  // const t = useTranslations();

  const closeSubViews = () => {
    setIsNotificationsOpen(false);
    setIsOrganisationSwitcherOpen?.(false);
  };

  const closeMobileMenus = () => {
    setIsMobileMenuOpen(false);
    setIsProfileMenuOpen(false);
    closeSubViews();
  };

  const mobileMenuToggle = () => {
    if (activeSubviewKey) {
      closeSubViews();
      setIsProfileMenuOpen(false);
      setIsMobileMenuOpen(true);
    } else {
      setIsProfileMenuOpen(false);
      setIsMobileMenuOpen(!isMobileMenuOpen);
    }
  };

  const profileMenuToggle = () => {
    if (activeSubviewKey) {
      closeSubViews();
      setIsMobileMenuOpen(false);
      setIsProfileMenuOpen(true);
    } else {
      setIsMobileMenuOpen(false);
      setIsProfileMenuOpen(!isProfileMenuOpen);
    }
  };

  const isMenuOpen = isMobileMenuOpen || isProfileMenuOpen;
  const visibleItems = isProfileMenuOpen ? profileItems : menuItems;
  const shouldShowProfileButton = Boolean(profileInitials && profileItems.length > 0);

  return (
    <>
      {/* Overlay */}
      <SidebarOverlay isOpen={isMenuOpen} onMobileMenuClose={closeMobileMenus} />

      <nav
        className={cn(
          'fixed top-0 right-0 left-0 z-50',
          isMenuOpen ? 'bg-sidebar' : 'bg-background'
        )}
      >
        <div className="relative mx-5 flex items-center justify-between gap-5 pt-6 pb-2">
          {/* Mobile Menu Button */}
          <Button
            className="group flex h-auto! w-auto! cursor-pointer items-center justify-center rounded-full p-0"
            variant="ghost"
            aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
            onClick={mobileMenuToggle}
          >
            {isMobileMenuOpen ? (
              <X className="text-sidebar-accent-foreground h-6! w-6!" />
            ) : (
              <Menu className="text-sidebar-foreground h-6! w-6! group-hover:text-sidebar-accent-foreground" />
            )}
          </Button>

          {/* Logo */}
          <Logo
            variant="horizontal"
            color="black"
            className="absolute top-1/2 left-1/2 h-[15px] w-[134px] -translate-x-1/2 -translate-y-1/2"
          />

          {shouldShowProfileButton ? (
            <button
              type="button"
              className={cn(
                'text-neutral-200 dark:text-neutral-100 flex size-10 cursor-pointer items-center justify-center rounded-full text-sm leading-5 focus-visible:ring-primary focus-visible:ring-2 focus-visible:outline-none',
                isProfileMenuOpen
                  ? 'bg-transparent'
                  : 'bg-grey-100 hover:bg-grey-200 dark:bg-grey-900 dark:hover:bg-grey-800'
              )}
              aria-label={isProfileMenuOpen ? 'Close profile menu' : 'Open profile menu'}
              onClick={profileMenuToggle}
            >
              {isProfileMenuOpen ? (
                <X className="text-sidebar-accent-foreground h-6! w-6!" />
              ) : (
                <p>{profileInitials}</p>
              )}
            </button>
          ) : (
            <div className="size-10" aria-hidden="true" />
          )}
        </div>

        {/* Mobile Menu Dropdown */}
        <div
          className={cn(
            'bg-sidebar absolute top-full right-0 left-0 max-h-[70vh] rounded-b-[20px] shadow-[0px_429px_120px_0px_rgba(42,43,65,0.00),0px_17px_38px_0px_rgba(42,43,65,0.05)] transition-all duration-300 ease-in-out',
            isMenuOpen
              ? activeSubviewKey
                ? 'h-[70vh] max-h-[70vh] overflow-visible'
                : 'max-h-unset overflow-y-auto opacity-100'
              : 'max-h-0 overflow-hidden opacity-0'
          )}
        >
          <div className="relative mx-5 my-5 flex flex-col gap-6">
            {visibleItems.map((item) => {
              const wrapperClassName = cn('flex flex-col gap-6', item.spaceAbove && 'mt-6');
              const isActiveSubviewItem = !activeSubviewKey || item.subviewKey === activeSubviewKey;
              const hiddenBySubview = activeSubviewKey && !isActiveSubviewItem;

              if (item.component) {
                return (
                  <div
                    className={cn(wrapperClassName, hiddenBySubview ? 'invisible' : '')}
                    key={item.title}
                  >
                    {item.bottomSeparated && (
                      <div className="bg-grey-100 dark:bg-grey-600 h-px w-full shrink-0" />
                    )}
                    {item.component}
                  </div>
                );
              }

              // Check if pathname matches any excluded prefix
              const isExcluded = item.excludeUrlPrefix
                ? Array.isArray(item.excludeUrlPrefix)
                  ? item.excludeUrlPrefix.some((prefix) => pathname.startsWith(prefix))
                  : pathname.startsWith(item.excludeUrlPrefix)
                : false;

              // Determine if menu item is active:
              // - If urlPrefix is set: check if pathname starts with it (and doesn't match any excludeUrlPrefix)
              // - Otherwise: check exact match with url
              const isActive = item.urlPrefix
                ? pathname.startsWith(item.urlPrefix) && !isExcluded
                : pathname === item.url;

              // Item with sub items: render parent + SidebarMenuSub
              if (item.items?.length) {
                // Exact pathname === sub.url required so submenu auto-opens on direct navigation (e.g. /payroll)
                const isParentActive = item.items.some((sub) =>
                  submenuMatchesPathname(pathname, sub)
                );
                const isSubMenuExpanded = expandedSubMenus.has(item.title);
                const visibleSubItems = isSubMenuExpanded
                  ? item.items
                  : isParentActive
                    ? item.items.filter(
                        (sub) =>
                          !sub.inactive && isSubmenuItemActive(pathname, sub.url, item.items!)
                      )
                    : item.items;
                const subMenuId = `mobile-submenu-${item.title.replace(/\s+/g, '-').toLowerCase()}`;
                return (
                  <div
                    key={item.title}
                    className={cn(
                      wrapperClassName,
                      'flex flex-col gap-1',
                      activeSubviewKey ? 'invisible' : ''
                    )}
                  >
                    <button
                      type="button"
                      onClick={() => !item.disabled && toggleSubMenu(item.title)}
                      disabled={item.disabled}
                      aria-expanded={isSubMenuExpanded || isParentActive}
                      aria-controls={subMenuId}
                      className={cn(
                        'group flex h-9 w-full items-center justify-between rounded-lg p-2 text-left text-sm text-sidebar-foreground',
                        'hover:bg-sidebar-accent',
                        item.disabled ? 'cursor-default opacity-50' : 'cursor-pointer'
                      )}
                    >
                      <div className="flex min-w-0 flex-1 items-center gap-2 overflow-visible">
                        {item.icon && (
                          <div className="relative">
                            <div
                              className={cn(
                                isSubMenuExpanded || isParentActive
                                  ? 'text-sidebar-accent-foreground'
                                  : 'group-hover:text-sidebar-accent-foreground'
                              )}
                            >
                              {item.icon}
                            </div>
                            {!isSubMenuExpanded &&
                            !isParentActive &&
                            typeof item.badge === 'number' &&
                            item.badge > 0 ? (
                              <span className="absolute -right-1 -top-1 flex h-4 w-4 min-w-4 items-center justify-center rounded-full bg-red-500 text-xs font-medium text-white">
                                {item.badge}
                              </span>
                            ) : null}
                          </div>
                        )}
                        <TruncateWithTooltip className="flex-1 text-sidebar-foreground">
                          {item.title}
                        </TruncateWithTooltip>
                      </div>
                      <ChevronDown
                        className={cn(
                          'h-4 w-4 shrink-0 transition-transform',
                          isSubMenuExpanded && 'rotate-180'
                        )}
                      />
                    </button>
                    {(isSubMenuExpanded || isParentActive) && visibleSubItems.length > 0 && (
                      <SidebarMenuSub
                        className="ml-4 mr-0 mt-2 gap-2 overflow-visible border-l border-grey-200 p-0"
                        id={subMenuId}
                      >
                        {visibleSubItems.map((sub) => {
                          const isInactive = Boolean(sub.inactive);
                          const isSubActive =
                            !isInactive && isSubmenuItemActive(pathname, sub.url, item.items!);
                          return (
                            <SidebarMenuSubItem
                              key={sub.title}
                              className={cn(
                                '-ml-px overflow-visible border-l border-grey-200 pl-0',
                                !isInactive && 'hover:border-pink-500',
                                isSubActive && 'bg-pink-50 dark:bg-grey-900 border-pink-500'
                              )}
                            >
                              {isInactive ? (
                                <SidebarMenuSubButton
                                  asChild
                                  className={cn(
                                    'active:bg-transparent data-[active=true]:bg-transparent',
                                    item.disabled && 'opacity-50'
                                  )}
                                >
                                  <span className="flex min-w-0 w-full items-center gap-2">
                                    <span className="w-2.5 shrink-0" />
                                    {sub.icon}
                                    <TruncateWithTooltip className="min-w-0 flex-1">
                                      {sub.title}
                                    </TruncateWithTooltip>
                                  </span>
                                </SidebarMenuSubButton>
                              ) : (
                                <SidebarMenuSubButton
                                  asChild
                                  isActive={isSubActive}
                                  className="overflow-visible p-4"
                                >
                                  <Link
                                    href={sub.url}
                                    className="flex min-w-0 w-full items-center justify-start"
                                    onClick={() => {
                                      closeMobileMenus();
                                      item.onClick?.();
                                    }}
                                  >
                                    {typeof sub.badge === 'number' && sub.badge > 0 && (
                                      <span className="absolute -left-2 top-2.5 flex h-4 min-w-4 w-4   items-center justify-center rounded-full bg-red-500 px-1.5 text-xs font-medium tabular-nums text-white">
                                        {sub.badge}
                                      </span>
                                    )}
                                    {sub.icon}
                                    <TruncateWithTooltip
                                      className={cn(
                                        'min-w-0 flex-1 text-start',
                                        isSubActive && 'ml-px'
                                      )}
                                    >
                                      {sub.title}
                                    </TruncateWithTooltip>
                                  </Link>
                                </SidebarMenuSubButton>
                              )}
                            </SidebarMenuSubItem>
                          );
                        })}
                      </SidebarMenuSub>
                    )}
                  </div>
                );
              }

              const linkClassName = cn(
                'group flex h-9 min-w-0 items-center gap-2 rounded-lg p-2 text-sm transition-colors',
                'hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
                'active:bg-sidebar-accent active:text-sidebar-accent-foreground',
                'data-[active=true]:bg-sidebar-accent data-[active=true]:text-sidebar-accent-foreground',
                isActive ? 'bg-sidebar-accent text-sidebar-accent-foreground' : '',
                activeSubviewKey ? 'invisible' : '',
                item.disabled ? 'cursor-default opacity-50' : ''
              );

              if (item.disabled) {
                return (
                  <div key={item.title} className={wrapperClassName}>
                    <div className={linkClassName} aria-disabled="true" role="menuitem">
                      {item.icon && (
                        <div
                          className={cn(
                            'text-sidebar-foreground',
                            isActive && 'text-sidebar-accent-foreground'
                          )}
                        >
                          {item.icon}
                        </div>
                      )}
                      <TruncateWithTooltip className="min-w-0 flex-1 text-sidebar-foreground">
                        {item.title}
                      </TruncateWithTooltip>
                    </div>
                  </div>
                );
              }

              return (
                <div key={item.title} className={wrapperClassName}>
                  <Link
                    href={item.url || ''}
                    className={linkClassName}
                    aria-current={isActive ? 'page' : undefined}
                    {...(item.openInNewTab
                      ? { target: '_blank' as const, rel: 'noopener noreferrer' }
                      : {})}
                    onClick={() => {
                      closeMobileMenus();
                      item.onClick?.();
                    }}
                  >
                    {item.icon && (
                      <div className="relative shrink-0">
                        <div
                          className={cn(
                            'text-sidebar-foreground group-hover:text-sidebar-accent-foreground',
                            isActive && 'text-sidebar-accent-foreground'
                          )}
                        >
                          {item.icon}
                        </div>
                        {typeof item.badge === 'number' && item.badge > 0 && (
                          <span className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-xs font-medium tabular-nums text-white">
                            {item.badge}
                          </span>
                        )}
                      </div>
                    )}
                    <TruncateWithTooltip className="min-w-0 flex-1 text-sidebar-foreground">
                      {item.title}
                    </TruncateWithTooltip>
                  </Link>
                </div>
              );
            })}
          </div>
        </div>
      </nav>
    </>
  );
}
